import React from 'react'

import styled, { css } from 'styled-components'

type GridDirection = 'row' | 'row-reverse' | 'column' | 'column-reverse'
type GridItemsAlignment = 'flex-start' | 'center' | 'flex-end' | 'stretch' | 'baseline'
type GridJustification = 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
type GridSize = 'auto' | true | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
type GridSpacing = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
type GridWrap = 'nowrap' | 'wrap' | 'wrap-reverse'

const BREAKPOINTS = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
}

export interface GridProps extends React.HTMLAttributes<HTMLDivElement> {
  alignItems?: GridItemsAlignment
  container?: boolean
  direction?: GridDirection
  item?: boolean
  justifyContent?: GridJustification
  lg?: GridSize
  md?: GridSize
  sm?: GridSize
  spacing?: GridSpacing
  tag?: 'div' | 'section' | 'article' | 'p'
  wrap?: GridWrap
  xl?: GridSize
  xs?: GridSize
}

function spacingStyles(container = false, spacing: GridSpacing = 0) {
  if (container) {
    return css`
      width: calc(100% + ${spacing * 4}px);
      margin: -${spacing * 2}px;
      --grid-item-padding: ${spacing * 2}px;
    `
  }

  return css`
    margin: 0;
    padding: var(--grid-item-padding, 0);
  `
}

function gridSizeStyles(size: GridSize) {
  if (size === 'auto') {
    return css`
      flex-grow: 0;
      max-width: none;
      flex-basis: auto;
    `
  }
  if (size === true) {
    return css`
      flex-grow: 1;
      max-width: 100%;
      flex-basis: 0;
    `
  }
  const percentage = (Number(size) / 12) * 100
  return css`
    flex-grow: 0;
    max-width: ${percentage}%;
    flex-basis: ${percentage}%;
  `
}

const StyledGrid = styled.div<GridProps>`
  display: ${({ container }) => (container ? 'flex' : 'block')};
  flex-wrap: ${({ wrap }) => wrap || 'wrap'};
  flex-direction: ${({ direction }) => direction || 'row'};
  align-items: ${({ alignItems }) => alignItems || 'stretch'};
  justify-content: ${({ justifyContent }) => justifyContent || 'flex-start'};

  box-sizing: border-box;
  margin: ${({ item }) => (item ? '0' : undefined)};

  ${({ container, spacing }) => spacingStyles(container, spacing)}

  ${({ xs }) => xs !== undefined && gridSizeStyles(xs)}

  @media (min-width: ${BREAKPOINTS.sm}px) {
    ${({ sm }) => sm !== undefined && gridSizeStyles(sm)}
  }

  @media (min-width: ${BREAKPOINTS.md}px) {
    ${({ md }) => md !== undefined && gridSizeStyles(md)}
  }

  @media (min-width: ${BREAKPOINTS.lg}px) {
    ${({ lg }) => lg !== undefined && gridSizeStyles(lg)}
  }

  @media (min-width: ${BREAKPOINTS.xl}px) {
    ${({ xl }) => xl !== undefined && gridSizeStyles(xl)}
  }
`

export const Grid = React.forwardRef<HTMLElement, GridProps>(function GridBase({ tag = 'div', ...rest }, ref) {
  return <StyledGrid as={tag} ref={ref} {...rest} />
})
